import { useState, useContext, useCallback, useEffect } from "react"
import { useLazyQuery, gql } from "@apollo/client"
import { FormContext } from "../../context/formContext"
import { Field, useFormikContext } from "formik"
import { useLocation } from "react-router-dom"
import { UserContext } from "../../context/userContext"
import fieldVisibility from "../../helpers/fieldVisibility"
import PhoneInput from "react-phone-number-input/input"
import { LogError } from "../../helpers/logger"

import styles from "../../styles/Form.module.css"

const INVALID_US_PHONE_NUMBER_TEXT =
  "Please enter a valid US phone number (maximum 11 digits)"

// Hook to auto-correct phone number value for US format
function useAutoCorrectPhone(fieldName, formik) {
  const [initialChecked, setInitialChecked] = useState(false)
  useEffect(() => {
    if (!initialChecked) {
      const value = formik.values[fieldName]
      if (value) {
        const cleaned = value.replace(/\D/g, "")
        let fixedValue = value

        if (cleaned.length === 10) {
          // US number without country code.
          fixedValue = "+1" + cleaned
        } else if (cleaned.length === 11 && cleaned.startsWith("1")) {
          // US number with '1' prefix.
          fixedValue = "+" + cleaned
        }

        if (fixedValue !== value) {
          formik.setFieldValue(fieldName, fixedValue)
        }
      }
      setInitialChecked(true)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [initialChecked, fieldName, formik.values[fieldName]])
}

// Ensures phone number starts with +1
// This is separate from auto-correction to avoid interfering with user input while typing
const ensureCountryPrefix = (value) => {
  if (value && !value.startsWith("+1")) {
    return "+1" + value
  }
  return value
}

// Validates US phone number format (must start with +1 and be 11 digits long)
const isValidPhoneNumber = (phoneNumber) => {
  if (!phoneNumber) return false
  const cleanedNumber = phoneNumber.replace(/\D/g, "")
  return phoneNumber.startsWith("+1") && cleanedNumber.length === 11
}

const validatePhoneFormat = (value) => {
  if (!value) return undefined
  if (!isValidPhoneNumber(value)) {
    return INVALID_US_PHONE_NUMBER_TEXT
  }
  return undefined
}

const clearFormatError = (formikRef, fieldName, fieldError) => {
  if (fieldError) {
    formikRef.setFieldError(fieldName, undefined)
  }
}

// Checks phone number uniqueness via GraphQL query.
// Format validation is handled centrally by Yup schema validators.
const validatePhoneNumber = async (
  value,
  checkPhone,
  typeQuery,
  userCtx,
  staffId,
) => {
  if (!value || !isValidPhoneNumber(value) || typeQuery === "") {
    return undefined
  }

  let resError
  try {
    let checkPhoneVars = {
      participant_phone_primary: value,
      organization_id: userCtx.session.user.organization_id,
      participant_id: userCtx.participant_id,
    }

    let user = userCtx.participant_id

    if (typeQuery === "staff") {
      checkPhoneVars = {
        staff_id: staffId,
        staff_phone_number: value,
      }

      user = staffId
    }

    if (user !== undefined) {
      const result = await checkPhone({
        variables: checkPhoneVars,
      })

      if (result.data && JSON.parse(result.data["phoneNumberVerifier"].data)) {
        if (typeQuery === "staff") {
          resError = `${value} is already in use by an existing staff member. Please provide a unique phone number or contact your organization administrator.`
        } else {
          resError = `${value} is already in use by an existing participant member. Please provide a unique phone number.`
        }
      }
    }
  } catch (error) {
    LogError("GraphQL query error:", error)
    resError =
      "An error occurred while validating the phone number. Error: " +
      (error.message || "Unknown error")
  }
  return resError
}

function RegularFieldOutput(props) {
  const {
    field,
    isRequired,
    block,
    index,
    checkPhone,
    typeQuery,
    userCtx,
    staffId,
  } = props
  const form = useContext(FormContext)
  const formik = useFormikContext()
  useAutoCorrectPhone(field.field_name, formik)

  let isDisabled = form ? form.blocksSaved[block] : false
  isDisabled = field.read_only ? true : isDisabled

  return (
    <div
      className={styles.field}
      style={{
        order: index,
        display: fieldVisibility(field.field_visibility),
      }}
    >
      <label className={styles.label}>
        {field.field_label}
        {isRequired && <span className={styles.asterisk}> *</span>}
      </label>
      {field.field_description && (
        <div className={styles.description}>{field.field_description}</div>
      )}
      <div className={styles.value}>
        <Field
          name={field.field_name}
          validate={async (value) =>
            value
              ? await validatePhoneNumber(
                  value,
                  checkPhone,
                  typeQuery,
                  userCtx,
                  staffId,
                )
              : undefined
          }
        >
          {({ field: formikField, meta, form }) => (
            <>
              <PhoneInput
                defaultCountry="US"
                placeholder="Enter phone number"
                disabled={isDisabled || props.loading}
                value={formikField.value}
                name={formikField.name}
                onChange={(value) => {
                  form.setFieldValue(
                    formikField.name,
                    ensureCountryPrefix(value),
                  )
                }}
                onBlur={formikField.onBlur}
                className="p-inputtext p-component"
              />
              {meta.touched && meta.error && !props.loading && (
                <div className={styles.errFieldMsg}>{meta.error}</div>
              )}
              {!meta.error &&
                formikField.value &&
                isValidPhoneNumber(formikField.value) &&
                !props.loading &&
                (meta.touched || formikField.value !== meta.initialValue) && (
                  <div className={styles.validFieldMsg}>
                    {formikField.value} is a valid US phone number
                  </div>
                )}
            </>
          )}
        </Field>
      </div>
    </div>
  )
}

function MultiFieldOutput(props) {
  const { field, isRequired, index, mfFieldName, mfIndex } = props
  const fieldSlug = `${mfFieldName}.${mfIndex}.${field.field_name}`
  const formik = useFormikContext()
  const isReadOnly = Boolean(field.read_only)
  useAutoCorrectPhone(fieldSlug, formik)

  return (
    <div className={styles.field} style={{ order: index }}>
      <label className={styles.label}>
        {field.field_label}
        {isRequired && <span className={styles.asterisk}> *</span>}
      </label>
      {field.field_description && (
        <div className={styles.description}>{field.field_description}</div>
      )}
      <div className={styles.value}>
        <Field name={fieldSlug} validate={validatePhoneFormat}>
          {({ field: fastField, meta, form: formik }) => (
            <>
              <PhoneInput
                country="US"
                placeholder="Enter phone number"
                disabled={isReadOnly || props.loading}
                value={fastField.value}
                name={fastField.name}
                onChange={(value) => {
                  formik.setFieldValue(
                    fastField.name,
                    ensureCountryPrefix(value),
                    false,
                  )

                  clearFormatError(formik, fastField.name, meta.error)
                }}
                onBlur={fastField.onBlur}
                className="p-inputtext p-component"
              />
              {meta.error && !props.loading && (
                <div className={styles.errFieldMsg}>{meta.error}</div>
              )}
              {!meta.error &&
                !props.loading &&
                fastField.value &&
                isValidPhoneNumber(fastField.value) &&
                (meta.touched || fastField.value !== meta.initialValue) && (
                  <div className={styles.validFieldMsg}>
                    {fastField.value} is a valid US phone number
                  </div>
                )}
            </>
          )}
        </Field>
      </div>
    </div>
  )
}

function NestedMultiFieldOutput(props) {
  const {
    field,
    isRequired,
    index,
    mfFieldName,
    mfIndex,
    nestedMfFieldName,
    nestedMfIndex,
  } = props
  const fieldSlug = `${mfFieldName}.${mfIndex}.${nestedMfFieldName}.${nestedMfIndex}.${field.field_name}`
  const formik = useFormikContext()
  const isReadOnly = Boolean(field.read_only)
  useAutoCorrectPhone(fieldSlug, formik)
  return (
    <div className={styles.field} style={{ order: index }}>
      <label className={styles.label}>
        {field.field_label}
        {isRequired && <span className={styles.asterisk}> *</span>}
      </label>
      {field.field_description && (
        <div className={styles.description}>{field.field_description}</div>
      )}
      <div className={styles.value}>
        <Field name={fieldSlug} validate={validatePhoneFormat}>
          {({ field: fastField, meta, form: formik }) => (
            <>
              <PhoneInput
                country="US"
                placeholder="Enter phone number"
                disabled={isReadOnly || props.loading}
                value={fastField.value}
                name={fastField.name}
                onChange={(value) => {
                  formik.setFieldValue(
                    fastField.name,
                    ensureCountryPrefix(value),
                    false,
                  )

                  clearFormatError(formik, fastField.name, meta.error)
                }}
                onBlur={fastField.onBlur}
                className="p-inputtext p-component"
              />
              {meta.touched && meta.error && !props.loading && (
                <div className={styles.errFieldMsg}>{meta.error}</div>
              )}
              {!meta.error &&
                !props.loading &&
                fastField.value &&
                isValidPhoneNumber(fastField.value) &&
                (meta.touched || fastField.value !== meta.initialValue) && (
                  <div className={styles.validFieldMsg}>
                    {fastField.value} is a valid US phone number
                  </div>
                )}
            </>
          )}
        </Field>
      </div>
    </div>
  )
}

export default function TypeTel(props) {
  const userCtx = useContext(UserContext)
  const form = useContext(FormContext)
  const location = useLocation()
  const isStaff = ["/form/staff"].some((path) => {
    const regex = new RegExp(path.replace(/\//g, "\\/"), "i")
    return regex.test(location.pathname)
  })
  const isOrganization = ["/form/organization"].some((path) => {
    const regex = new RegExp(path.replace(/\//g, "\\/"), "i")
    return regex.test(location.pathname)
  })
  const isEnterprise = [
    "/form/enterprise",
    "/library/enterprise_profile/",
  ].some((path) => {
    const regex = new RegExp(path.replace(/\//g, "\\/"), "i")
    return regex.test(location.pathname)
  })

  const isParticipantPhonePrimary =
    props?.field?.idField === "participant_phone_primary"
  const isParticipant =
    isStaff === false &&
    isOrganization === false &&
    isEnterprise === false &&
    isParticipantPhonePrimary

  useEffect(() => {
    // Register field as loading in FormContext
    form && form.registerLoadingField(props?.field)

    // Notify FormContext that field is loaded
    form && form.completeLoadingField(props?.field)

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const STAFF_PHONE_NUMBER_VERIFIER = gql`
    query staffPhoneNumberVerifier(
      $staff_id: ID!
      $staff_phone_number: String!
    ) {
      phoneNumberVerifier: staffPhoneNumberVerifier(
        staff_phone_number: $staff_phone_number
        staff_id: $staff_id
      ) {
        data
      }
    }
  `

  const PHONE_NUMBER_VERIFIER = gql`
    query phoneNumberVerifier(
      $participant_phone_primary: String!
      $organization_id: String!
      $participant_id: String!
    ) {
      phoneNumberVerifier(
        participant_phone_primary: $participant_phone_primary
        organization_id: $organization_id
        participant_id: $participant_id
      ) {
        data
      }
    }
  `

  let phoneQuery
  let typeQuery

  if (isStaff) {
    phoneQuery = STAFF_PHONE_NUMBER_VERIFIER
    typeQuery = "staff"
  } else if (isParticipant) {
    phoneQuery = PHONE_NUMBER_VERIFIER
    typeQuery = "participant"
  } else {
    phoneQuery = STAFF_PHONE_NUMBER_VERIFIER
    typeQuery = ""
  }

  const [checkPhone, { loading }] = useLazyQuery(phoneQuery, {
    onError: (error) => {
      LogError("GraphQL query error:", error)
    },
  })

  // Get staffId from formik context if available
  let staffId = null
  try {
    const formik = useFormikContext()
    const selectedStaffId =
      formik?.getFieldProps && formik.getFieldProps("selected_staff_id")
    staffId = selectedStaffId ? selectedStaffId.value : null
  } catch (e) {
    staffId = null
  }

  const phoneCheckProps = {
    checkPhone,
    loading,
    userCtx,
    staffId,
  }

  const checkIfRequired = () => {
    try {
      return JSON.parse(props.field.field_validation.required[0])
    } catch (error) {
      return false
    }
  }

  const isRequired = checkIfRequired()

  switch (props.type) {
    case "regular-field": {
      const regFieldProps = {
        field: props.field,
        isRequired,
        block: props.block,
        index: props.index,
        typeQuery,
        staffId,
        ...phoneCheckProps,
      }
      return <RegularFieldOutput {...regFieldProps} />
    }
    case "multi-field": {
      const multiFieldProps = {
        field: props.field,
        isRequired,
        index: props.index,
        mfFieldName: props.mfFieldName,
        mfIndex: props.mfIndex,
        typeQuery,
        staffId,
        ...phoneCheckProps,
      }
      return <MultiFieldOutput {...multiFieldProps} />
    }
    case "nested-multi-field": {
      const nestedMultiFieldProps = {
        field: props.field,
        isRequired,
        index: props.index,
        mfFieldName: props.mfFieldName,
        mfIndex: props.mfIndex,
        nestedMfFieldName: props.nestedMfFieldName,
        nestedMfIndex: props.nestedMfIndex,
        typeQuery,
        staffId,
        ...phoneCheckProps,
      }
      return <NestedMultiFieldOutput {...nestedMultiFieldProps} />
    }
    default:
      return null
  }
}
